import React, { useCallback, useEffect, useState } from "react";

import {
  Box,
  Input,
  Typography,
  Paper,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DeleteIcon from "@mui/icons-material/Delete";
import YouTubeIcon from "@mui/icons-material/YouTube";
import RssFeedIcon from "@mui/icons-material/RssFeed";

import { formatEpisodeLengthToHumanFriendlyDuration } from "../../../../utils/formatEpisodeLengthToHumanFriendlyDuration";
import { formatFileSize } from "../../../../utils/formatFileSize";
import YouTubeImport from "../YouTubeImport";
import RssImport, { RssEpisodeSelection } from "../RssImport";
import { YouTubeVideoMetadata } from "../../services";

export type SourceMode = "file" | "youtube" | "rss";

interface UploadProps {
  setFiles: (files: File[]) => void;
  files: File[];
  sourceMode: SourceMode;
  onSourceModeChange: (mode: SourceMode) => void;
  youtubeMetadata: YouTubeVideoMetadata | null;
  onYouTubeMetadataChange: (metadata: YouTubeVideoMetadata | null) => void;
  rssEpisode: RssEpisodeSelection | null;
  onRssEpisodeChange: (selection: RssEpisodeSelection | null) => void;
}

const selectedFilePaperSx: SxProps<Theme> = {
  p: 2.5,
  borderRadius: "12px",
  border: "1px solid",
  borderColor: "divider",
  backgroundColor: (theme) =>
    theme.palette.mode === "light"
      ? theme.palette.grey[50]
      : theme.palette.grey[900],
};

export default function Upload({
  setFiles,
  files,
  sourceMode,
  onSourceModeChange,
  youtubeMetadata,
  onYouTubeMetadataChange,
  rssEpisode,
  onRssEpisodeChange,
}: UploadProps) {
  const [fileDurationInSeconds, setFileDurationInSeconds] = useState<number>(0);
  const [durationError, setDurationError] = useState<string>("");
  const MIN_DURATION_SECONDS = 120;

  useEffect(() => {
    if (files.length === 0) {
      setFileDurationInSeconds(0);
      return;
    }
    const d = (files[0] as { duration_in_seconds?: number })
      .duration_in_seconds;
    if (typeof d === "number" && d > 0 && !Number.isNaN(d)) {
      setFileDurationInSeconds(d);
    }
  }, [files]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const mediaElement = file.type.startsWith("audio")
        ? new Audio(URL.createObjectURL(file))
        : document.createElement("video");

      mediaElement.src = URL.createObjectURL(file);

      mediaElement.addEventListener("loadedmetadata", () => {
        const { duration } = mediaElement;
        setFileDurationInSeconds(duration);

        if (duration < MIN_DURATION_SECONDS) {
          const minutes = Math.floor(MIN_DURATION_SECONDS / 60);
          setDurationError(
            `File duration must be at least ${minutes} minute${minutes > 1 ? "s" : ""}.\nCurrent duration: ${formatEpisodeLengthToHumanFriendlyDuration(duration)}`,
          );
          setFiles([]);
          URL.revokeObjectURL(mediaElement.src);
          return;
        }

        setDurationError("");
        setFiles(
          acceptedFiles.map((currentFile) =>
            Object.assign(currentFile, {
              duration_in_seconds: duration,
              preview: URL.createObjectURL(currentFile),
            }),
          ),
        );
      });
    },
    [setFiles],
  );

  const handleRemoveFile = () => {
    setFiles([]);
    setFileDurationInSeconds(0);
    setDurationError("");
  };

  const handleSourceModeChange = (
    _: React.MouseEvent,
    newMode: SourceMode | null,
  ) => {
    if (!newMode) return;
    onSourceModeChange(newMode);
    setFiles([]);
    setDurationError("");
    onYouTubeMetadataChange(null);
    onRssEpisodeChange(null);
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    accept: {
      "video/*": [],
      "audio/*": [],
    },
  });

  const selectedFile = files[0] as File & {
    path?: string;
    duration_in_seconds?: number;
  };
  const displayName = selectedFile?.name ?? selectedFile?.path ?? "";
  const isAudio = selectedFile?.type.startsWith("audio");

  return (
    <Box sx={{ width: "100%" }}>
      {/* Source mode toggle */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
        <ToggleButtonGroup
          value={sourceMode}
          exclusive
          onChange={handleSourceModeChange}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              px: 2.5,
              py: 0.75,
              borderRadius: "8px !important",
              border: "1px solid",
              borderColor: "divider",
              color: "text.secondary",
              "&.Mui-selected": {
                backgroundColor: "#2563eb",
                color: "#fff",
                borderColor: "#2563eb",
                "&:hover": { backgroundColor: "#1d4ed8" },
              },
            },
            gap: 1,
            border: "none",
            "& .MuiToggleButtonGroup-grouped": {
              border: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          <ToggleButton value="file" aria-label="Upload file">
            <CloudUploadIcon sx={{ fontSize: 16, mr: 0.75 }} />
            Upload File
          </ToggleButton>
          <ToggleButton value="youtube" aria-label="Import from YouTube">
            <YouTubeIcon sx={{ fontSize: 16, mr: 0.75 }} />
            Import from YouTube
          </ToggleButton>
          <ToggleButton value="rss" aria-label="Import from RSS feed">
            <RssFeedIcon sx={{ fontSize: 16, mr: 0.75 }} />
            RSS Feed
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* File upload mode */}
      {sourceMode === "file" &&
        (!files.length ? (
          <Paper
            elevation={0}
            sx={{
              border: "2px dashed",
              borderColor: "#2563eb",
              borderRadius: "12px",
              p: { xs: 4, sm: 6 },
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "#eff6ff",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: "#dbeafe",
                borderColor: "#1d4ed8",
              },
            }}
            {...getRootProps({ className: "dropzone" })}
          >
            <Input inputProps={{ ...getInputProps() }} />
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "16px",
                backgroundColor: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Typography
              sx={{
                mb: 0.75,
                color: "text.primary",
                fontWeight: 700,
                fontSize: "15px",
              }}
            >
              Upload Your Episode
            </Typography>
            <Typography
              sx={{ color: "text.secondary", mb: 1.5, fontSize: "13px" }}
            >
              Drag and drop your audio or video file here, or click to browse
            </Typography>
            <Typography
              sx={{ color: "text.secondary", fontSize: "12px", mb: 1 }}
            >
              Supports MP3, WAV, MP4, MOV and other common formats
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "#fff",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
                px: 1.5,
                py: 0.5,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 14, color: "#2563eb" }} />
              <Typography
                sx={{ fontSize: "12px", fontWeight: 500, color: "#2563eb" }}
              >
                Minimum duration: 2 minutes
              </Typography>
            </Box>
            {durationError ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  p: 1.5,
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fca5a5",
                  borderRadius: "8px",
                  textAlign: "left",
                }}
              >
                <ErrorOutlineIcon
                  sx={{ color: "#dc2626", fontSize: 16, flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    color: "#dc2626",
                    fontWeight: 500,
                    fontSize: "12px",
                    whiteSpace: "pre-line",
                  }}
                >
                  {durationError}
                </Typography>
              </Box>
            ) : null}
          </Paper>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="h6"
              sx={{
                mb: 1,
                textAlign: "left",
                color: "#23272E",
                fontWeight: 700,
                fontSize: "1.125rem",
              }}
            >
              Selected file
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 2, color: "text.secondary", lineHeight: 1.5 }}
            >
              Review your file, then continue to episode details. You can remove
              it and choose another at any time.
            </Typography>

            <Paper elevation={0} sx={selectedFilePaperSx}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "10px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isAudio ? "#eff6ff" : "#fef2f2",
                  }}
                >
                  {isAudio ? (
                    <AudioFileIcon sx={{ color: "#2563eb", fontSize: 24 }} />
                  ) : (
                    <VideoFileIcon sx={{ color: "#dc2626", fontSize: 24 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#23272E",
                      wordBreak: "break-word",
                    }}
                  >
                    {displayName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 0.5 }}
                  >
                    {selectedFile?.type?.split("/")[1]?.toUpperCase() ?? "—"}
                    {" · "}
                    {fileDurationInSeconds > 0
                      ? formatEpisodeLengthToHumanFriendlyDuration(
                          fileDurationInSeconds,
                        )
                      : "—"}
                    {" · "}
                    {formatFileSize(selectedFile?.size ?? 0, 1)}
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <Chip
                      icon={
                        <CheckCircleIcon sx={{ fontSize: "18px !important" }} />
                      }
                      label="Ready to continue"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(22, 101, 52, 0.08)",
                        color: "#15803d",
                        fontWeight: 600,
                        borderRadius: "8px",
                        "& .MuiChip-icon": { color: "#15803d" },
                      }}
                    />
                  </Box>
                </Box>
                <IconButton
                  aria-label="Remove file"
                  onClick={handleRemoveFile}
                  size="small"
                  sx={{
                    color: "text.secondary",
                    flexShrink: 0,
                    mt: -0.5,
                    "&:hover": {
                      backgroundColor: "rgba(220, 38, 38, 0.08)",
                      color: "#dc2626",
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Paper>

            {durationError ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  p: 1.5,
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fca5a5",
                  borderRadius: "8px",
                }}
              >
                <ErrorOutlineIcon
                  sx={{ color: "#dc2626", fontSize: 16, flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    color: "#dc2626",
                    fontWeight: 500,
                    fontSize: "12px",
                    whiteSpace: "pre-line",
                  }}
                >
                  {durationError}
                </Typography>
              </Box>
            ) : null}
            {fileRejections.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  p: 1.5,
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: "8px",
                }}
              >
                <ErrorOutlineIcon
                  sx={{ color: "#d97706", fontSize: 16, flexShrink: 0 }}
                />
                <Typography
                  sx={{ color: "#92400e", fontWeight: 500, fontSize: "12px" }}
                >
                  {fileRejections[0].errors[0].code === "too-many-files"
                    ? "You can upload only one file at a time."
                    : "Please select a valid audio or video file."}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ))}

      {/* YouTube import mode */}
      {sourceMode === "youtube" && (
        <YouTubeImport
          youtubeMetadata={youtubeMetadata}
          onYouTubeMetadataChange={onYouTubeMetadataChange}
        />
      )}

      {/* RSS import mode */}
      {sourceMode === "rss" && (
        <RssImport
          selectedEpisode={rssEpisode}
          onEpisodeSelect={onRssEpisodeChange}
        />
      )}
    </Box>
  );
}
